import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";

function GoogleCalendarSyncLogDialog({ open, onClose, organizationId, t }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const pageSize = 15;

  const fetchLogs = useCallback(async () => {
    if (!organizationId || !open) return;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      if (statusFilter) {
        params.append("status", statusFilter);
      }

      const response = await axios.get(
        `/api/organizations/${organizationId}/google-calendar/sync-logs/?${params}`
      );

      setLogs(response.data.logs || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      console.error("Failed to fetch sync logs:", err);
      setError(t("googleCalendar.errorFetchingLogs"));
    } finally {
      setLoading(false);
    }
  }, [organizationId, open, page, statusFilter, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const getStatusChip = (status) => {
    const config = {
      pending: { label: t("googleCalendar.pending"), color: "warning" },
      success: { label: t("googleCalendar.success"), color: "success" },
      failed: { label: t("googleCalendar.failed"), color: "error" },
      skipped: { label: t("googleCalendar.skipped"), color: "default" },
    };

    const { label, color } = config[status] || { label: status, color: "default" };
    return <Chip label={label} color={color} size="small" />;
  };

  const getActionLabel = (action) => {
    const labels = {
      create: t("googleCalendar.create"),
      update: t("googleCalendar.update"),
      delete: t("googleCalendar.delete"),
    };
    return labels[action] || action;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: "80vh" } }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">{t("googleCalendar.syncLog")}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton size="small" onClick={fetchLogs} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t("googleCalendar.syncLogDescription")}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Filter */}
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t("googleCalendar.filterByStatus")}</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleFilterChange}
              label={t("googleCalendar.filterByStatus")}
            >
              <MenuItem value="">{t("All")}</MenuItem>
              <MenuItem value="success">{t("googleCalendar.success")}</MenuItem>
              <MenuItem value="failed">{t("googleCalendar.failed")}</MenuItem>
              <MenuItem value="pending">{t("googleCalendar.pending")}</MenuItem>
              <MenuItem value="skipped">{t("googleCalendar.skipped")}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        ) : logs.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {t("googleCalendar.noLogs")}
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("Date")}</TableCell>
                    <TableCell>{t("googleCalendar.item")}</TableCell>
                    <TableCell>{t("googleCalendar.action")}</TableCell>
                    <TableCell>{t("googleCalendar.status")}</TableCell>
                    <TableCell>{t("googleCalendar.error")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {log.object_description}
                        </Typography>
                      </TableCell>
                      <TableCell>{getActionLabel(log.action)}</TableCell>
                      <TableCell>{getStatusChip(log.status)}</TableCell>
                      <TableCell>
                        {log.error_message && (
                          <Tooltip title={log.error_message} arrow>
                            <IconButton size="small" color="error">
                              <ErrorIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(GoogleCalendarSyncLogDialog);
